import React, { useEffect, useState } from 'react'
import MuiDrawer from '@mui/material/Drawer'
import { Box, Button, FormControl, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material'
import { Close, Add, CircleOutlined } from '@mui/icons-material'
import styled from 'styled-components'
import { copy } from '../../../../../../../icon/copy'
import { trash } from '../../../../../../../icon/trash'
import { tick } from '../../../../../../../icon/tick'
import { remove } from '../../../../../../../icon/remove'
import { useFormik } from 'formik'
import * as Yup from 'yup'


const StyledDrawer = styled(MuiDrawer).withConfig({ shouldForwardProp: (prop) => prop !== 'open' })<{ open: boolean; }>`
  height: 100%;
  width: ${p => p.open ? 'auto' : `0`};
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1200;

  .MuiDrawer-paper {
    position: relative;
    transition: width 0.5s ease;
  }

  ${p => p.open && `
    &::before {
      content: '';
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(2px);
      z-index: -1;
    }
  `}
`

interface Answer {
	text: string;
	explanation: string;
	isAnswer: boolean;
	answer?: string;
	is_correct?: boolean;
}

interface Question {
	question: string;
	answers: Answer[];
}

interface IAnswer {
	text: string;
	explanation: string;
	isAnswer: boolean;
}

interface IQuestion {
	question: string;
	answers: IAnswer[];
	isEditable: boolean;
}

interface FormValues {
	name: string;
	questions: {
		isEditable?: boolean;
		question: string;
		answers: {
			text: string;
			isAnswer: boolean;
			explanation?: string;
		}[];
	}[];
}

const ChapterTestDrawer = ({
	                           setActiveDrawer,
	                           activeDrawer,
	                           test,
	                           open,
	                           close,
	                           onSaveTest,
	                           contentIndex
                           }: {
	setActiveDrawer: (data: any) => void,
	activeDrawer: string,
	contentIndex: any,
	selectedChapterId: string,
	test?: any,
	open: boolean,
	onSaveTest: (data: any) => void,
	close: (e?: string) => void
}) => {
	//eslint-disable-next-line
	const [invalid, setInvalid] = useState(false)
	const [editingAnswer, setEditingAnswer] = useState<{
		questionIndex: number | null,
		answerIndex: number | null
	}>({ questionIndex: null, answerIndex: null })
	const [editingExplanation, setEditingExplanation] = useState<{
		questionIndex: number | null,
		answerIndex: number | null
	}>({ questionIndex: null, answerIndex: null })
	const [editingSettings, setEditingSettings] = useState(false)
	const [tempExplanation, setTempExplanation] = useState('')
	
	const validationSchema = Yup.object().shape({
		name: Yup.string()
			.trim(' ')
			.max(200, 'Максимальная длина названия статьи - 200 символов')
			.required('Требуется название теста'),
		questions: Yup.array()
			.of(
				Yup.object().shape({
					question: Yup.string()
						.trim(' ')
						.max(200, 'Максимальная длина текст вопроса - 200 символов')
						.required('Требуется текст вопроса'),
					answers: Yup.array()
						.of(
							Yup.object().shape({
								text: Yup.string().required('Требуется текст ответа'),
								isAnswer: Yup.boolean(),
								explanation: Yup.string()
							})
						)
						.min(1, 'Необходим хотя бы один ответ')
						.required('Необходимо указать варианты ответов')
				})
			)
			.min(1, 'Необходим хотя бы один вопрос')
			.required('Необходимо добавить вопросы')
			.test(
				'has-correct-answer',
				'Для каждого вопроса должен быть выбран хотя бы один правильный ответ',
				questions => questions.every(question =>
					question.answers.some(answer => answer.isAnswer)
				)
			)
	})
	
	const initialValues: FormValues = {
		name: '',
		questions: []
	}
	
	const formik = useFormik({
		initialValues,
		validationSchema: validationSchema,
		onSubmit: (values) => {
			onSaveTest({
				test_name: values.name,
				questions: values.questions.map((q) => ({
					question: q.question,
					answers: q.answers.map((a) => ({
						answer: a.text,
						is_correct: a.isAnswer,
						explanation: a.explanation || ''
					}))
				}))
			})
			close()
		}
	})
	
	useEffect(() => {
		if (activeDrawer === 'test' && contentIndex !== -1 && test) {
			formik.setValues({
				name: test.test_name,
				questions: test.questions.map((q: Question) => ({
					...q,
					answers: q.answers.map((a: Answer) => ({
						text: a.answer,
						explanation: a.explanation || '',
						isAnswer: a.is_correct
					}))
				}))
			})
		} else {
			return
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	
	const handleClose = (value?: any) => {
		close(value)
		setActiveDrawer('')
	}
	
	const addAnswer = (questionIndex: number) => {
		const questionsCopy = [...formik.values.questions]
		const newAnswer = {
			text: `Вариант ${questionsCopy[questionIndex].answers.length + 1}`,
			explanation: '',
			isAnswer: false
		}
		questionsCopy[questionIndex].answers.push(newAnswer)
		formik.setFieldValue('questions', questionsCopy)
	}
	
	const startEditing = (questionIndex: number, answerIndex: number) => {
		setEditingAnswer({ questionIndex, answerIndex })
	}
	const addExplanation = (questionIndex: number, answerIndex: number) => {
		setEditingExplanation({ questionIndex, answerIndex })
		setTempExplanation(formik.values.questions[questionIndex].answers[answerIndex].explanation || '')
	}
	
	const saveExplanation = (questionIndex: number, answerIndex: number) => {
		formik.setFieldValue(`questions[${questionIndex}].answers[${answerIndex}].explanation`, tempExplanation)
		setTempExplanation('')
		setEditingExplanation({ questionIndex: null, answerIndex: null })
	}
	
	const handleAddQuestion = () => {
		const newQuestion: IQuestion = {
			question: '',
			answers: [{ text: 'Вариант 1', explanation: '', isAnswer: false }],
			isEditable: true
		}
		const newQuestions = [...formik.values.questions, newQuestion]
		formik.setFieldValue('questions', newQuestions)
	}
	const handleRemoveQuestion = (questionIndex: number) => {
		const updatedQuestions = formik.values.questions.filter((_, index) => index !== questionIndex)
		formik.setFieldValue('questions', updatedQuestions)
	}
	const toggleQuestionEditable = (index: number) => {
		const questionsCopy = [...formik.values.questions]
		
		if (typeof questionsCopy[index].isEditable !== 'undefined') {
			questionsCopy[index].isEditable = !questionsCopy[index].isEditable
			formik.setFieldValue('questions', questionsCopy)
		}
		if (!questionsCopy[index].isEditable) {
			setEditingSettings(false)
		}
	}
	const copyQuestion = (index: number) => {
		const questions = formik.values.questions
		const toCopy = questions[index]
		const questionCopy = JSON.parse(JSON.stringify(toCopy))
		const newQuestions = [...questions, questionCopy]
		formik.setFieldValue('questions', newQuestions)
	}
	const removeAnswer = (questionIndex: number, answerIndex: number) => {
		const questionsCopy = [...formik.values.questions]
		questionsCopy[questionIndex].answers = questionsCopy[questionIndex].answers.filter((_, idx) => idx !== answerIndex)
		formik.setFieldValue('questions', questionsCopy)
	}
	
	function hasCorrectAnswerSelected(answers: IAnswer[]): boolean {
		return answers.some(answer => answer.isAnswer)
	}
	
	function questionError(questionIndex: number, formik: any): boolean {
		const error = formik.errors.questions?.[questionIndex]
		const touched = formik.touched.questions?.[questionIndex]?.question
		return Boolean(touched && error && typeof error === 'object' && error.question)
	}
	
	function questionHelperText(questionIndex: number, formik: any): string | undefined {
		if (formik.touched.questions?.[questionIndex]?.question && formik.errors.questions?.[questionIndex] && typeof formik.errors.questions[questionIndex] !== 'string') {
			return formik.errors.questions[questionIndex]?.question
		}
		return undefined
	}
	
	return (
		<StyledDrawer variant='permanent'
		              anchor='right'
		              open={open}
		              sx={{
			              right: 0,
			              top: 0,
			              position: 'fixed',
			              zIndex: 2,
			              background: '#fff'
		              }}
		>
			<div className='chapter-drawer-close' onClick={() => handleClose()}><Close /></div>
			<div className='chapter-drawer-content'>
				<div className='constructor-content-title'>
					Добавить тест
				</div>
				<div className='lecture-name'>
					Название теста
				</div>
				<TextField
					variant='outlined'
					placeholder='Введите название'
					name='name'
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					value={formik.values.name}
					error={formik.touched.name && Boolean(formik.errors.name)}
					helperText={formik.touched.name ? formik.errors.name : ''}
					className='without-label'
				/>
				<div
					className={formik.values.name.length > 200 ? 'description-input-counter error-text' : 'description-input-counter'}>{formik.values.name.length}/200
				</div>
				
				<div className='lecture-type-select-wrapper'>
					<div className='lecture-type-select-description'>
						Вы можете добавить до 12 вопросов . Обязательно укажите правильный вариант ответа, а также
						описание почему определенный ответ не может быть верным.
					</div>
					
					{formik.values.questions?.map((item: any, questionIndex: number) =>
						<div key={'q' + questionIndex} className='question-wrapper'>
							<div className='question-header'>
								<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
									<div className='step-three-questions'>Вопрос {questionIndex + 1}</div>
									<Box sx={{ display: 'flex', gap: '8px' }}>
										{formik.values.questions.length < 12 &&
											<button className='delete-button-step-three-task copy'
											        onClick={() => copyQuestion(questionIndex)}
											>
												{copy}
											</button>
										}
										<button className='delete-button-step-three-task copy'
										        onClick={() => handleRemoveQuestion(questionIndex)}>
											{trash}
										</button>
									</Box>
								</Box>
							
							</div>
							<TextField
								variant='outlined'
								placeholder='Введите вопрос'
								name={`questions[${questionIndex}].question`}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.questions[questionIndex].question}
								error={questionError(questionIndex, formik)}
								helperText={questionHelperText(questionIndex, formik)}
								className='without-label'
								sx={{ marginBottom: '32px' }}
							/>
							
							
							<div className='answers-header'>
								Варианты ответов
								<div className='answers-settings' onClick={() => setEditingSettings(true)}>Настройки
									ответов</div>
							</div>
							
							<FormControl disabled={!item.isEditable} error={invalid}
							             sx={{ width: '100%', background: 'transparent' }}>
								<RadioGroup
									name={`questions[${questionIndex}].answers`}
									sx={{ marginBottom: '15px', gap: '15px' }}
									aria-labelledby='demo-controlled-radio-buttons-group'
									value={formik.values.questions[questionIndex].answers.findIndex(a => a.isAnswer).toString()}
									onChange={(event) => {
										const newAnswers = formik.values.questions[questionIndex].answers.map((answer, idx) => ({
											...answer,
											isAnswer: parseInt(event.target.value) === idx
										}))
										
										formik.setFieldValue(`questions[${questionIndex}].answers`, newAnswers)
									}}
								
								>
									{item.answers?.map((answer: any, answerIndex: number) => (
										<>
											<div className='radio-wrapper custom-radio-group'
											     key={`question-${questionIndex}-answer-${answerIndex}`}
											     onDoubleClick={() => startEditing(questionIndex, answerIndex)}>
												<Box sx={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center'
												}}>
													<Box>
														{editingAnswer.questionIndex === questionIndex && editingAnswer.answerIndex === answerIndex ? (
															<div className='edit-answer'>
																<CircleOutlined />
																<TextField
																	variant='standard'
																	defaultValue={formik.values.questions[questionIndex].answers[answerIndex].text}
																	inputProps={{
																		style: { paddingLeft: '8px' }
																	}}
																	sx={{
																		background: 'transparent',
																		marginLeft: '4px',
																		'& .MuiInput-underline:before': {
																			borderBottom: 'none'
																		},
																		'& .MuiInput-underline:hover:not(.Mui-disabled):before': {
																			borderBottom: 'none'
																		},
																		'& .MuiInput-underline:after': {
																			borderBottom: 'none'
																		}
																	}}
																	onChange={e => {
																		const path = `questions[${questionIndex}].answers[${answerIndex}].text`
																		formik.setFieldValue(path, e.target.value)
																	}}
																/>
																<Button
																	onClick={() => setEditingAnswer({ questionIndex: null, answerIndex: null })}><Close /></Button>
															</div>
														) : (
															<FormControlLabel
																sx={{ margin: 0 }} value={answerIndex.toString()}
																control={<Radio sx={{ padding: 0 }} />}
																label={formik.values.questions[questionIndex].answers[answerIndex].text} />
														)}
													</Box>
													<Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
														<Box>
															{answer.isAnswer && <div>{tick}</div>}
														</Box>
														<Button
															onClick={() => removeAnswer(questionIndex, answerIndex)}>
															{remove}
														</Button>
													</Box>
												</Box>
												{answer.explanation && !editingSettings && (
													<div className='explanation'>
														{answer.explanation}
													</div>
												)}
											</div>
											{editingSettings && (
												
												editingExplanation.questionIndex === questionIndex && editingExplanation.answerIndex === answerIndex ?
													<div className='edit-explanation'>
														<TextField
															placeholder='Введите пояснение почему ответ неверный'
															variant='outlined'
															value={tempExplanation}
															onChange={(e) => setTempExplanation(e.target.value)}
															onBlur={formik.handleBlur}
															className='without-label'
														/>
														<Button
															variant='outlined'
															sx={{
																alignSelf: 'flex-end',
																marginTop: '12px',
																background: '#FFF'
															}}
															onClick={() => saveExplanation(questionIndex, answerIndex)}
														>
															Сохранить
														</Button>
													</div> :
													(
														answer.explanation ? <div className='explanation'
														                          onDoubleClick={() => addExplanation(questionIndex, answerIndex)}>{answer.explanation}</div>
															:
															<div className='add-explanation'
															     onClick={() => addExplanation(questionIndex, answerIndex)}>
																<Add /><span>Добавить пояснение</span></div>
													)
											)}
										
										</>
									
									))}
								</RadioGroup>
								{formik.values && (
									<div className='add-answer' onClick={() => addAnswer(questionIndex)}>
										{formik.values.questions[questionIndex].answers.length < 12 && (
											<><CircleOutlined /><span>Добавить вариант</span></>
										)}
									</div>
								)}
								{!hasCorrectAnswerSelected(item.answers) && formik.touched.questions?.[questionIndex] && formik.errors.questions?.[questionIndex] && (
									<div style={{ color: 'red', marginTop: '10px', fontSize: '12px' }}>
										Для этого вопроса не выбран правильный ответ
									</div>
								)}
							</FormControl>
							{editingSettings && (
								<div className='parent-container'>
									<button
										className='add-button-step-three-task'
										onClick={() => toggleQuestionEditable(questionIndex)}
									>
										{formik.values.questions[questionIndex].isEditable ? 'Готово' : 'Редактировать'}
									</button>
								</div>
							)}
						</div>
					)}
					{formik.values.questions.length < 12 &&
						<Button sx={{
							color: '#0573E4',
							fontSize: '14px',
							fontWeight: '600',
							marginTop: '32px',
							textTransform: 'none'
						}}
						        className='add-field'
						        onClick={handleAddQuestion}
						        variant='text'><Add />Добавить вопрос</Button>
					}
				</div>
			</div>
			{activeDrawer === 'test' && (
				<div className='chapter-drawer-footer-Test'>
					<Button
						variant='contained'
						onClick={(e) => {
							e.preventDefault()
							formik.handleSubmit()
						}}>
						Сохранить
					</Button>
				</div>
			)}
		</StyledDrawer>
	)
}
export default ChapterTestDrawer
